import type { ReactNode } from 'react';
import { CalendarClock, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/atoms/Button';
import { Checkbox } from '@/components/ui/atoms/Checkbox';
import { Divider } from '@/components/ui/atoms/Divider';
import { Input } from '@/components/ui/atoms/Input';
import { Typography } from '@/components/ui/atoms/Typography';
import { CandidateIdentity } from '../candidate-actions/CandidateIdentity';
import {
  canOfferSaveAsDefault,
  isValidBookingUrl,
  type BookingLinkDraft,
  type SavedBookingLink,
} from '@/utils/bookingLink';

export interface InterviewInvitationFieldsProps {
  recipientEmail?: string | null;
  candidateName?: string;
  draft: BookingLinkDraft;
  onDraftChange: (updater: (current: BookingLinkDraft) => BookingLinkDraft) => void;
  saved: SavedBookingLink | null;
  isLoading: boolean;
  saveAsDefault: boolean;
  onSaveAsDefaultChange: (value: boolean) => void;
  checkboxId: string;
  /** When false, copy must not promise an email and booking options are hidden. */
  willNotify?: boolean;
  /** Per-move "send email" checkbox; booking options nest under it. */
  notificationToggle?: ReactNode;
}

export function InterviewInvitationFields({
  recipientEmail,
  candidateName,
  draft,
  onDraftChange,
  saved,
  isLoading,
  saveAsDefault,
  onSaveAsDefaultChange,
  checkboxId,
  willNotify = true,
  notificationToggle,
}: InterviewInvitationFieldsProps) {
  const { t } = useTranslation('candidates');
  const name = candidateName?.trim();
  const showEditor = draft.include && (draft.editing || !saved);
  const offerSaveAsDefault = canOfferSaveAsDefault(draft, saved);
  const saveAsDefaultId = `${checkboxId}-save-default`;

  return (
    <div className="space-y-4">
      <CandidateIdentity
        name={name || recipientEmail || t('invitation_popover.not_available')}
        email={name ? recipientEmail : null}
      />

      {notificationToggle ?? (
        <Typography variant="body-sm" color="secondary" as="p">
          {t(
            willNotify
              ? 'invitation_popover.consequence_on'
              : 'invitation_popover.consequence_off'
          )}
        </Typography>
      )}

      {willNotify && notificationToggle ? <Divider /> : null}

      {willNotify ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={checkboxId}
              checked={draft.include}
              disabled={isLoading}
              onChange={(event) =>
                onDraftChange((current) => ({ ...current, include: event.target.checked }))
              }
              size="md"
            />
            <label
              htmlFor={checkboxId}
              className="text-sm font-body font-medium text-dark-invirtus cursor-pointer"
            >
              {t('invitation_popover.include_booking_link')}
            </label>
          </div>

          <div className="flex flex-col gap-2">
            {!draft.include && (
              <Typography variant="body-xs" color="secondary" as="p">
                {t('invitation_popover.booking_link_excluded')}
              </Typography>
            )}

            {draft.include && !showEditor && saved ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <CalendarClock className="h-4 w-4 shrink-0 text-dark-invirtus/60" />
                  <div className="min-w-0">
                    <p className="text-xs font-body font-medium text-dark-invirtus/60">
                      {saved.provider_label}
                    </p>
                    <p className="truncate text-sm font-body text-dark-invirtus" title={saved.url}>
                      {saved.url}
                    </p>
                  </div>
                </div>
                <Button
                  type="icon"
                  intent="muted"
                  size="sm"
                  icon={<Pencil />}
                  title={t('invitation_popover.edit_booking_link')}
                  aria-label={t('invitation_popover.edit_booking_link')}
                  onClick={() => onDraftChange((current) => ({ ...current, editing: true }))}
                />
              </div>
            ) : null}

            {showEditor ? (
              <>
                <Input
                  type="url"
                  value={draft.url}
                  onChange={(event) =>
                    onDraftChange((current) => ({ ...current, url: event.target.value }))
                  }
                  placeholder={t('invitation_popover.booking_link_placeholder')}
                  size="md"
                  rounded="lg"
                  autoFocus
                />
                {offerSaveAsDefault ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={saveAsDefaultId}
                      checked={saveAsDefault}
                      onChange={(event) => onSaveAsDefaultChange(event.target.checked)}
                      size="md"
                    />
                    <label
                      htmlFor={saveAsDefaultId}
                      className="text-xs font-body text-dark-invirtus/70 cursor-pointer"
                    >
                      {t('invitation_popover.save_as_default')}
                    </label>
                  </div>
                ) : (
                  <Typography variant="body-xs" color="secondary" as="p">
                    {saved
                      ? t('invitation_popover.booking_link_hint')
                      : t('invitation_popover.booking_link_first_time_hint')}
                  </Typography>
                )}
                {draft.url.trim().length > 0 && !isValidBookingUrl(draft.url) ? (
                  <p className="text-xs font-body text-red-600">
                    {t('invitation_popover.booking_link_invalid')}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
